import React from "react";
import { Button, Form, useForm, Modal, Loader, useNotify } from "ebs-design";
import { ModalProps } from "ebs-design/dist/components/organisms/Modal/Modal";
import { UseMutationResult } from "react-query";
import { AxiosError } from "axios";
import "react-quill-2/dist/quill.snow.css";

import {
  AttachmentsFormField,
  Editor,
  Flex,
  LoadingButton,
  PDFViewer,
  PreviewTabs,
  PreviewTabsEntity,
  PreviewTabsState,
  WhiteSpace,
} from "@aeo/core/components";
import { generatePreviewPdfArrayBuffer } from "@aeo/core/utils";
import models from "@aeo/core/models";

export interface EditorModalProps extends ModalProps {
  request?: models.ApplicationRequest;
  data?: {
    name?: string;
    action?: string;
    initialValue?: {
      name?: string;
      content?: string;
    };
    period?: boolean;
    templateId?: string;
    headerTemplateId?: string;
  };
  mutation?: UseMutationResult<unknown, AxiosError, any, unknown>;
  withAttachments?: boolean;
  previewMutation?: UseMutationResult<unknown, AxiosError, any, unknown>;
  pdfFile?: ArrayBuffer;
  setPdfFile?: (pdf?: ArrayBuffer) => void;
  omitHeader?: boolean;
}

export const EditorModal = ({
  data,
  request,
  mutation,
  previewMutation,
  withAttachments,
  pdfFile,
  setPdfFile,
  omitHeader,
  ...props
}: EditorModalProps) => {
  const [form] = useForm();
  const [editorState, setEditorState] = React.useState<string>();
  const [currentTab, setCurrentTab] =
    React.useState<PreviewTabsState>("editor");
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const [localPdfFile, setLocalPdfFile] = React.useState<ArrayBuffer>();
  const previewTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const notify = useNotify();

  React.useEffect(
    () => setEditorState(data?.initialValue?.content || ""),
    [data?.initialValue?.content],
  );

  const handleClick = () => {
    mutation?.mutate({
      content: editorState,
      ...(withAttachments && {
        attachments: form.getFieldValue("attachments"),
      }),
    });
  };

  const handlePreview = React.useCallback(async () => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    setIsPreviewing(true);
    try {
      const shouldIncludeHeader = !omitHeader;

      // Get attachments from form if withAttachments is enabled
      let contentWithAttachments = editorState || "";

      if (withAttachments) {
        const attachments = form.getFieldValue("attachments");
        if (attachments && attachments.length > 0) {
          // Append attachments list to content
          contentWithAttachments += `
            <h3 class="documents" style="font-weight: bold; margin-top: 20px;">Documente atașate:</h3>
            ${attachments.map((att: any, index: number) => `
              <p>${index + 1}. ${att.title || att.name || '-'}</p>
            `).join('')}
          `;
        }
      }

      const buffer = await generatePreviewPdfArrayBuffer({
        content: contentWithAttachments,
        header: shouldIncludeHeader ? "use-header" : "",
        title: data?.initialValue?.name || data?.name || "Preview",
        options: {
          cacheKey: `${data?.name || "editor"}:${editorState}:${form.getFieldValue("attachments")?.length || 0}`,
        },
      });
      setLocalPdfFile(buffer);
      setPdfFile?.(buffer);
    } catch (error) {
      setLocalPdfFile(undefined);
      notify.error({ title: "Nu a putut fi generat preview-ul PDF." });
    } finally {
      setIsPreviewing(false);
    }
  }, [data?.initialValue?.name, data?.name, editorState, notify, setPdfFile, withAttachments, form]);

  const previewTabs: PreviewTabsEntity = {
    editor: {
      tabKey: "editor",
      title: "Editare",
      onClick: () => setCurrentTab("editor"),
      content: (
        <Form form={form}>
          <Editor value={editorState} onChange={setEditorState} />
          {withAttachments && (
            <div className="py-15">
              <AttachmentsFormField />
            </div>
          )}
        </Form>
      ),
    },
    preview: {
      tabKey: "preview",
      title: "Vizualizare",
      onClick: () => {
        handlePreview();
        setCurrentTab("preview");
        setEditorState(editorState);
      },
      content: (
        <div className="py-20">
          <Loader loading={isPreviewing}>
            <PDFViewer file={localPdfFile || pdfFile} />
          </Loader>
        </div>
      ),
    },
  };

  return (
    <Modal
      title={data?.initialValue?.name || data?.name || ""}
      {...props}
      className="editor-modal"
      size="large"
      closeOnClickOutside={false}
    >
      <Modal.Content className="p-0">
        <PreviewTabs tabs={previewTabs} currentTab={currentTab} />
      </Modal.Content>
      <Modal.Footer>
        <Flex justify="flex-end">
          <Button
            onClick={() => {
              props?.onClose?.();
              setPdfFile?.(undefined);
            }}
          >
            Anulează
          </Button>
          <WhiteSpace h="1rem" />
          <LoadingButton
            type="primary"
            onClick={handleClick}
            loading={mutation?.isLoading}
          >
            Transmite
          </LoadingButton>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
